export const AUDIT_CATEGORIES = [
  { value: 'AUTH', label: '인증·보안' },
  { value: 'REPORT', label: '분석·리포트' },
  { value: 'ADMIN', label: '관리자' }
]

// 서버 AuditEventType과 같은 순서다. 새 종류가 생기면 서버 enum과 함께 추가한다.
export const AUDIT_EVENT_TYPES = [
  { value: 'LOGIN', label: '로그인', category: 'AUTH' },
  { value: 'LOGOUT', label: '로그아웃', category: 'AUTH' },
  { value: 'SESSION_REPLACED', label: '중복 로그인 세션 교체', category: 'AUTH' },
  { value: 'PASSWORD_RESET_REQUESTED', label: '비밀번호 재설정 요청', category: 'AUTH' },
  { value: 'PASSWORD_RESET_VERIFIED', label: '비밀번호 재설정 인증', category: 'AUTH' },
  { value: 'PASSWORD_RESET_COMPLETED', label: '비밀번호 재설정 완료', category: 'AUTH' },
  { value: 'ADMIN_ACCESS_DENIED', label: '권한 없는 관리자 접근', category: 'AUTH' },
  { value: 'ANALYSIS_REQUESTED', label: '분석 접수', category: 'REPORT' },
  { value: 'ANALYSIS_COMPLETED', label: '분석 완료', category: 'REPORT' },
  { value: 'ANALYSIS_FAILED', label: '분석 실패', category: 'REPORT' },
  { value: 'REPORT_VIEWED', label: '리포트 열람', category: 'REPORT' },
  { value: 'REPORT_PDF_PREVIEWED', label: 'PDF 미리보기', category: 'REPORT' },
  { value: 'REPORT_PDF_DOWNLOADED', label: 'PDF 다운로드 요청', category: 'REPORT' },
  { value: 'REPORT_DOWNLOAD_CLICKED', label: '다운로드 버튼 클릭', category: 'REPORT' },
  { value: 'RECOMMENDED_AMOUNT_TEMPLATE_DOWNLOADED', label: '권장금액 Excel 다운로드', category: 'REPORT' },
  { value: 'ADMIN_REPORT_RESULTS_VIEWED', label: '전체 결과이력 조회', category: 'ADMIN' },
  { value: 'HEALTH_COVERAGE_LINK_CREATED', label: '건강-보장 연결 추가', category: 'ADMIN' },
  { value: 'HEALTH_COVERAGE_LINK_UPDATED', label: '건강-보장 연결 수정', category: 'ADMIN' },
  { value: 'HEALTH_COVERAGE_LINK_DELETED', label: '건강-보장 연결 삭제', category: 'ADMIN' }
]

const OUTCOME_LABELS = { SUCCESS: '성공', FAILURE: '실패', DENIED: '거부' }

const CHANGE_FIELD_LABELS = {
  areaCode: '영역',
  areaLabel: '영역명',
  coverageCode: '보장',
  coverageName: '보장명',
  relationType: '연결 종류',
  sortOrder: '순서',
  enabled: '사용'
}

export function categoryLabel(value) {
  return AUDIT_CATEGORIES.find((category) => category.value === value)?.label || value || '-'
}

export function eventTypeLabel(value) {
  return AUDIT_EVENT_TYPES.find((type) => type.value === value)?.label || value || '-'
}

export function outcomeLabel(value) {
  return OUTCOME_LABELS[value] || value || '-'
}

/** 분류를 고르면 그 분류의 이벤트만 고를 수 있다. */
export function eventTypesForCategory(category) {
  return category ? AUDIT_EVENT_TYPES.filter((type) => type.category === category) : AUDIT_EVENT_TYPES
}

/** 행위자 표시. 로그인 전 이벤트는 입력한 아이디나 이메일로 보여 준다. */
export function describeActor(event) {
  if (event.actorName || event.actorLoginId) {
    const name = event.actorName || `#${event.actorCustomerId}`
    return event.actorLoginId ? `${name} (${event.actorLoginId})` : name
  }
  if (event.actorCustomerId) return `#${event.actorCustomerId}`
  if (event.attemptedLoginId) return `입력 아이디: ${event.attemptedLoginId}`
  if (event.email) return event.email
  return '-'
}

export function describeTarget(event) {
  const owner = event.ownerName && event.ownerCustomerId !== event.actorCustomerId
    ? ` · 소유자 ${event.ownerName}`
    : ''
  if (!event.targetType) return owner ? owner.slice(3) : '-'
  return `${event.targetType} ${event.targetId || ''}`.trim() + owner
}

function formatValue(value) {
  if (value === null || value === undefined || value === '') return '없음'
  if (value === true) return '예'
  if (value === false) return '아니오'
  return String(value)
}

/** 변경 전후 값 중 바뀐 필드만 "필드: 전 → 후" 줄로 만든다. */
export function describeChanges(changes) {
  if (!changes || typeof changes !== 'object') return []
  return Object.entries(changes).map(([field, change]) => (
    `${CHANGE_FIELD_LABELS[field] || field}: ${formatValue(change?.before)} → ${formatValue(change?.after)}`
  ))
}

/** 상세 칸에 보여 줄 짧은 설명 줄. */
export function describeDetail(event) {
  const detail = event.detail || {}
  const lines = []
  if (event.email && (event.actorName || event.actorCustomerId)) lines.push(`이메일: ${event.email}`)
  if (detail.templateId) lines.push(`템플릿: ${detail.templateId}`)
  if (detail.reason) lines.push(`사유: ${detail.reason}`)
  if (detail.path) lines.push(`${detail.method || ''} ${detail.path}`.trim())
  if (detail.reportId) lines.push(`리포트: ${detail.reportId}`)
  if (detail.previousSessionCreatedAt) lines.push(`이전 세션 시작: ${detail.previousSessionCreatedAt}`)
  if (detail.resultCount !== undefined) lines.push(`${(detail.page ?? 0) + 1}쪽 · ${detail.resultCount}건`)
  if (detail.healthFiles !== undefined) {
    lines.push(`검진 ${detail.healthFiles} · 보험 ${detail.insuranceFiles} · 권장금액 ${detail.supportingFiles}`)
  }
  lines.push(...describeChanges(detail.changes))
  if (event.errorCode) lines.push(`오류: ${event.errorCode}`)
  return lines
}
